import {
  Workflow, WorkflowStep, WorkflowCondition, WorkflowStepType,
  ApprovalStepConfiguration, AutoAssignmentConfiguration, HstProvisioningConfiguration, StepConfiguration,
  CreateWorkflowRequest, UpdateWorkflowRequest,
} from './types';

// ── Factory ────────────────────────────────────────────────────────

export const createDefaultWorkflow = (): Workflow => ({
  id: '',
  name: '',
  description: '',
  trigger: '',
  isActive: true,
  createdBy: '',
  createdDate: new Date().toISOString(),
  conditions: [],
  conditionOperator: 'AND',
  steps: [],
});

export const createDefaultApprovalConfig = (): ApprovalStepConfiguration => ({
  approverEmail: '',
  backupApproverEmail: '',
  timeoutDuration: 0,
  timeoutUnit: 'Hours',
  requireComment: false,
  approvalMessage: '',
  reminderIntervalHours: null,
});

export const createDefaultAssignmentRule = () => ({
  type: 'Specific' as const,
  targetUser: '',
  targetUserName: '',
  targetTeam: '',
  userPool: [] as string[],
  requiredSkills: [] as string[],
  maxWorkload: 10,
});

export const createDefaultAutoAssignConfig = (): AutoAssignmentConfiguration => ({
  assignmentRule: createDefaultAssignmentRule(),
});

export const createDefaultHstProvisioningConfig = (): HstProvisioningConfiguration => ({});

export const STEP_TYPE_LABELS: Record<WorkflowStepType, string> = {
  [WorkflowStepType.ApprovalStep]: 'Approval',
  [WorkflowStepType.AutoAssignmentStep]: 'Auto Assignment',
  [WorkflowStepType.HstProvisioningStep]: 'HST provisioning',
};

export const configurationForStepType = (stepType: WorkflowStepType): StepConfiguration => {
  switch (stepType) {
    case WorkflowStepType.ApprovalStep: return createDefaultApprovalConfig();
    case WorkflowStepType.AutoAssignmentStep: return createDefaultAutoAssignConfig();
    case WorkflowStepType.HstProvisioningStep: return createDefaultHstProvisioningConfig();
  }
};

const DEFAULT_STEPS: Record<WorkflowStepType, { name: string; description: string }> = {
  [WorkflowStepType.ApprovalStep]: { name: 'Request approval', description: 'Request approval before proceeding' },
  [WorkflowStepType.AutoAssignmentStep]: { name: 'Auto-assign ticket', description: 'Automatically assign the ticket based on rules' },
  [WorkflowStepType.HstProvisioningStep]: { name: 'HST provisioning', description: 'Create or update the HST user after approval' },
};

export const createStepId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

export const createDefaultStep = (stepType: WorkflowStepType, order: number = 1): WorkflowStep => ({
  id: createStepId(),
  order,
  isRequired: true,
  timeout: 300,
  stepType,
  ...DEFAULT_STEPS[stepType],
  configuration: configurationForStepType(stepType),
});

export const createEmptyCondition = (): WorkflowCondition => ({
  field: '',
  operator: '',
  value: '',
});

// ── Request Builders ───────────────────────────────────────────────

const mapStepsForRequest = (steps: WorkflowStep[]) =>
  steps.map(({ id, order, stepType, name, description, configuration }) => ({
    id,
    order,
    stepType,
    name,
    description,
    // Serialization boundary — StepConfiguration → Record<string, any> for the API request
    configuration: configuration as unknown as Record<string, any>,
  }));

export const toCreateRequest = (workflow: Workflow): CreateWorkflowRequest => ({
  name: workflow.name.trim(),
  description: workflow.description.trim(),
  trigger: workflow.trigger,
  isActive: workflow.isActive,
  conditionOperator: workflow.conditionOperator,
  conditions: workflow.conditions,
  steps: mapStepsForRequest(workflow.steps),
});

export const toUpdateRequest = (workflow: Workflow, id: string): UpdateWorkflowRequest => ({
  ...toCreateRequest(workflow),
  id,
});
